import { Link } from 'react-router-dom';
import { MarketplaceArche } from '../../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArcheIconImg, DefaultArcheIcon } from '@/lib/arche-icon-url';
import { archePath } from '@/lib/arche-url';

interface ArcheCardProps {
  arche: MarketplaceArche;
}

export function ArcheCard({ arche }: ArcheCardProps) {
  const hasBaseCost = arche.pricing.priceCents != null && arche.pricing.priceCents > 0;
  const hasActionPricing =
    arche.actionPricing != null && Object.keys(arche.actionPricing).length > 0;
  const hasUsageFees = arche.hasPerUseCredits || hasActionPricing;

  const pricingLabel = hasBaseCost
    ? hasUsageFees
      ? `$${(arche.pricing.priceCents! / 100).toFixed(2)} + usage fees`
      : `$${(arche.pricing.priceCents! / 100).toFixed(2)}`
    : hasUsageFees
      ? 'Pay per use'
      : 'Free';

  return (
    <Link to={`/marketplace/arches/${archePath(arche)}`} className="block h-full">
      <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow flex flex-col">
        <CardContent className="p-4 flex flex-col flex-1">
          {/* Square icon */}
          <div className="flex justify-center mb-3">
            {arche.imageRef ? (
              <ArcheIconImg
                imageRef={arche.imageRef}
                size={128}
                alt={arche.name}
                className="w-16 h-16 rounded-2xl object-cover shadow-sm"
              />
            ) : (
              <DefaultArcheIcon className="w-16 h-16 rounded-2xl shadow-sm" />
            )}
          </div>

          {/* Name */}
          <h3 className="text-sm font-semibold text-foreground text-center line-clamp-2 mb-1">
            {arche.name}
          </h3>

          <p className="text-sm text-muted-foreground line-clamp-3 mb-3 min-h-[3.75rem] text-center">
            {arche.simpleDescription}
          </p>

          <div className="flex items-center justify-between text-xs text-muted-foreground mb-2 mt-auto">
            {arche.categoryName && <Badge variant="outline">{arche.categoryName}</Badge>}
            <div className="flex items-center gap-3">
              <span title="Fias Fitness Score">FFS: {(arche.fitnessScore * 100).toFixed(0)}%</span>
              <span title="Times Used">{arche.invocationCount.toLocaleString()} runs</span>
            </div>
          </div>

          <div className="flex justify-end gap-1.5">
            <Badge variant={!hasBaseCost && !hasUsageFees ? 'secondary' : 'default'}>
              {pricingLabel}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
